/**
 * screens/admin/adminSigningKeys.tsx — N-2 · Signing key register.
 *
 * INTERNAL CONSOLE ONLY. SKPLATFORM-UICC-006 §8 / DB-006 S4.
 *
 * A TABLE, not cards. This is a reference surface read during an incident, and
 * during an incident people scan — the `kid` here is the thing a reader is
 * matching against a decoded token.
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────
 *
 * No delete action. Keys RETIRE; they do not disappear. Rows are never
 * removed, because a disputed token must always be attributable to a key and
 * a window — so retired rows collapse behind a toggle instead, default off.
 *
 * No private key material, and there is nothing to render: a CHECK constraint
 * means the database physically cannot hold a private component.
 *
 * ── TWO STATES THAT MUST SHOUT ────────────────────────────────────────────
 *
 * 1. A `local_dev` backend row. Production refuses to BOOT on it, so if this
 *    screen ever renders one, something is very wrong — a full-width critical
 *    banner says so rather than a polite badge.
 * 2. A `customer`-realm row. After the interim path retires, Platform mints no
 *    customer tokens; a customer row here means it still does. Warning banner.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { PSwitch, PEmpty } from '../../components/platform/controls';
import Alert from '../../components/alert';
import { FS, FW } from '../../utils/platformTokens';
import { listSigningKeys } from '../../services/platform/platformApi';
import type { SigningKeyRow } from '../../schema/platform/types';

type Status = 'Current' | 'Overlapping' | 'Retired';

function statusOf(k: SigningKeyRow): Status {
  if (k.retired_at) return 'Retired';
  if (k.is_current) return 'Current';
  // Not current, not yet retired: still verifies. That IS the rotation
  // overlap, expressed as a state rather than as a second "current" key.
  return 'Overlapping';
}

const AdminSigningKeys: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [keys, setKeys] = useState<SigningKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setKeys(await listSigningKeys().catch(() => [])); }
      finally { setLoading(false); }
    })();
  }, []);

  const localDev = keys.filter((k) => k.backend === 'local_dev' && !k.retired_at);
  const customerRealm = keys.filter((k) => k.realm === 'customer' && !k.retired_at);

  const rows = useMemo(
    () => keys.filter((k) => (showRetired ? true : !k.retired_at)),
    [keys, showRetired],
  );

  const columns: Column<SigningKeyRow>[] = [
    {
      key: 'kid',
      header: 'kid',
      render: (k) => (
        <Text
          selectable
          style={{ fontSize: FS.sm, color: c.ink, fontFamily: theme.font.mono, textDecorationLine: k.retired_at ? 'line-through' : 'none' }}
        >
          {k.kid}
        </Text>
      ),
    },
    {
      key: 'realm',
      header: 'Realm',
      width: 110,
      render: (k) => <Tag tone={k.realm === 'customer' ? 'warn' : 'neutral'}>{k.realm}</Tag>,
    },
    {
      key: 'alg',
      header: 'Algorithm',
      width: 110,
      render: (k) => (
        <Text style={{ fontSize: FS.sm, color: c.ink2, fontFamily: theme.font.mono }}>{k.algorithm}</Text>
      ),
    },
    {
      key: 'backend',
      header: 'Backend',
      width: 130,
      render: (k) => (
        <Tag tone={k.backend === 'local_dev' ? 'danger' : k.backend === 'gcp_kms' ? 'success' : 'neutral'}>
          {k.backend}
        </Tag>
      ),
    },
    {
      key: 'active_from',
      header: 'Active from',
      width: 150,
      render: (k) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {new Date(k.activated_at).toISOString().slice(0, 16).replace('T', ' ')}
        </Text>
      ),
    },
    {
      key: 'retire_after',
      header: 'Retire after',
      width: 150,
      render: (k) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {k.retire_after ? new Date(k.retire_after).toISOString().slice(0, 16).replace('T', ' ') : '—'}
        </Text>
      ),
    },
    {
      /**
       * Status is the column people are looking for, so it earns the accent.
       * Exactly one Current per realm, ever — enforced by a partial unique
       * index, not by this screen.
       */
      key: 'status',
      header: 'Status',
      width: 130,
      render: (k) => {
        const s = statusOf(k);
        if (s === 'Current') return <Tag dot tone="accent">Current</Tag>;
        if (s === 'Overlapping') return <Tag tone="neutral">Overlapping</Tag>;
        return (
          <Text style={{ fontSize: FS.xs, color: c.ink4, textDecorationLine: 'line-through' }}>
            Retired
          </Text>
        );
      },
    },
  ];

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1280, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Signing key register"
        sub="Every key that has ever signed a Platform token. Public material and key-service references only — Softknack internal."
      />

      {/* State 1. Production refuses to boot on a local dev key, so rendering
          one here means something is very wrong. Say so, loudly. */}
      {localDev.length > 0 && (
        <View style={{ marginBottom: 16 }}>
          <Alert tone="danger">
            A development signing key is active. Production refuses to start on this backend, so
            this should be impossible — every token signed by {localDev.map((k) => k.kid).join(', ')}{' '}
            is forgeable. Investigate immediately.
          </Alert>
        </View>
      )}

      {/* State 2. Platform should mint no customer token at all once the
          interim path retires. */}
      {customerRealm.length > 0 && (
        <View style={{ marginBottom: 16 }}>
          <Alert tone="warn">
            A customer-realm key is present. Platform should mint no customer token once the
            interim customer path is retired — customer identity is SK Passport&apos;s.
          </Alert>
        </View>
      )}

      <Panel>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
          <Text style={{ fontSize: FS.xs, color: c.ink3, flex: 1 }}>
            Keys are retired, never deleted — a disputed token must always resolve to a key and a
            window.
          </Text>
          <PSwitch checked={showRetired} onChange={setShowRetired} label="Show retired" />
        </View>

        <DataTable
          columns={columns}
          rows={rows}
          keyExtractor={(k) => k.kid}
          minWidth={980}
          empty={
            <PEmpty
              title={loading ? 'Loading the register…' : 'No keys recorded'}
              children={
                <Text style={{ fontSize: FS.sm, color: c.ink3 }}>
                  Run the key backfill so every issued kid resolves to a row.
                </Text>
              }
            />
          }
        />
      </Panel>

      {/* Public JWK behind a disclosure, formatted as mono JSON. For gcp_kms
          rows the resource path is shown — a path, not a secret — and is
          selectable so it can be copied into a console. */}
      {rows.length > 0 && (
        <>
          <View style={{ height: 16 }} />
          <Panel title="Public key material" sub="Public JWK, and the key-service resource path where one applies.">
            {rows.map((k) => (
              <View key={k.kid} style={{ borderTopWidth: 1, borderTopColor: c.line, paddingVertical: 10 }}>
                <Text
                  onPress={() => setExpanded(expanded === k.kid ? null : k.kid)}
                  style={{ fontSize: FS.sm, color: c.accent, fontFamily: theme.font.mono, fontWeight: FW.medium }}
                >
                  {expanded === k.kid ? '▾' : '▸'} {k.kid}
                </Text>
                {expanded === k.kid && (
                  <View style={{ marginTop: 8, gap: 8 }}>
                    {k.kms_resource_name && (
                      <Text selectable style={{ fontSize: FS.xs, color: c.ink2, fontFamily: theme.font.mono }}>
                        {k.kms_resource_name}
                      </Text>
                    )}
                    <Text selectable style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono, lineHeight: 18 }}>
                      {JSON.stringify(k.public_jwk, null, 2)}
                    </Text>
                  </View>
                )}
              </View>
            ))}
          </Panel>
        </>
      )}
    </ScrollView>
  );
};

export default AdminSigningKeys;
